import React, { useState, useEffect } from 'react';
import ApiService from '../services/api';
import './UsernameModal.css';

const UsernameModal = ({ isOpen, onClose, user, updateUser, onSuccess }) => {
  const [username, setUsername] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState(null);
  const [errors, setErrors] = useState({});

  const isCreating = !user?.username;
  const hasReachedLimit = user?.username_changes_count >= 3;

  useEffect(() => {
    if (isOpen) {
      setUsername(user?.username || '');
      setErrors({});
      setAvailabilityStatus(null);
    }
  }, [isOpen, user]);

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/[\s-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 50);
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    const slug = generateSlug(value);
    setUsername(slug);
    setErrors({});
    setAvailabilityStatus(null);
  };

  const checkAvailability = async () => {
    if (!username || username === user?.username) {
      setAvailabilityStatus(null);
      return;
    }

    if (username.length < 3) {
      setAvailabilityStatus({ available: false, message: 'Username must be at least 3 characters' });
      return;
    }

    setIsCheckingAvailability(true);
    try {
      const response = await ApiService.post('/profile/check-username', { username });
      setAvailabilityStatus({
        available: response.available,
        message: response.message
      });
    } catch (error) {
      setAvailabilityStatus({
        available: false,
        message: 'Failed to check availability'
      });
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username) {
      setErrors({ username: 'Username is required' });
      return;
    }

    if (username.length < 3) {
      setErrors({ username: 'Username must be at least 3 characters' });
      return;
    }

    if (username === user?.username) {
      onClose();
      return;
    }

    if (availabilityStatus && !availabilityStatus.available) {
      setErrors({ username: availabilityStatus.message });
      return;
    }

    if (hasReachedLimit && !isCreating) {
      setErrors({ username: 'You have reached the maximum number of username changes (3)' });
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const response = await ApiService.put('/profile/username', { username });
      
      if (response.success) {
        updateUser({
          ...user,
          username: response.data.username,
          username_changes_count: response.data.username_changes_count,
          remaining_username_changes: response.data.remaining_changes
        });
        
        const successMsg = isCreating 
          ? '✅ Username created successfully!' 
          : '✅ Username updated successfully!';
        if (onSuccess) {
          onSuccess(successMsg);
        }
        onClose();
      } else {
        setErrors({ username: response.message || 'Failed to update username' });
      }
    } catch (error) {
      console.error('Username update error:', error);
      setErrors({ 
        username: error.response?.data?.message || 'Failed to update username. Please try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content username-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isCreating ? 'Create Username' : 'Edit Username'}</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="modal-body">
          <div className="username-info">
            <p>
              {isCreating 
                ? 'Create a unique username for your public profile URL:' 
                : 'Your username will be used in your public profile URL:'
              }
            </p>
            <div className="url-preview">
              <span className="url-base">{window.location.origin}/talent/</span>
              <span className="url-username">{username || 'your-username'}</span>
            </div>
            {isCreating && (
              <div className="creation-hint">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 6v6l4 2"></path>
                </svg>
                <span>Choose wisely! You can only change it 3 times.</span>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="username-form">
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <div className="input-group">
                <span className="input-prefix">@</span>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={handleUsernameChange}
                  onBlur={checkAvailability}
                  placeholder="your-username"
                  disabled={isLoading}
                  className={`form-input ${errors.username ? 'error' : ''}`}
                />
                {isCheckingAvailability && (
                  <div className="checking-indicator">
                    <div className="spinner"></div>
                  </div>
                )}
              </div>
              
              {availabilityStatus && (
                <div className={`availability-status ${availabilityStatus.available ? 'available' : 'unavailable'}`}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {availabilityStatus.available ? (
                      <path d="M20 6L9 17l-5-5"></path>
                    ) : (
                      <path d="M18 6L6 18M6 6l12 12"></path>
                    )}
                  </svg>
                  <span>{availabilityStatus.message}</span>
                </div>
              )}

              {errors.username && (
                <div className="error-message">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="15" y1="9" x2="9" y2="15"></line>
                    <line x1="9" y1="9" x2="15" y2="15"></line>
                  </svg>
                  <span>{errors.username}</span>
                </div>
              )}
            </div>

            <div className="username-rules">
              <h4>Username Rules:</h4>
              <ul>
                <li>3-50 characters long</li>
                <li>Only lowercase letters, numbers, and hyphens</li>
                <li>Must be unique</li>
                <li>{isCreating ? 'You can change it up to 3 times after creation' : 'You can change it up to 3 times total'}</li>
              </ul>
            </div>

            {user?.username_changes_count !== undefined && !isCreating && (
              <div className="changes-info">
                <div className="changes-counter">
                  <span>Changes used: {user.username_changes_count}/3</span>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${(user.username_changes_count / 3) * 100}%` }}
                    ></div>
                  </div>
                </div>
                {hasReachedLimit && (
                  <div className="limit-reached">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="15" y1="9" x2="9" y2="15"></line>
                      <line x1="9" y1="9" x2="15" y2="15"></line>
                    </svg>
                    <span>You have reached the maximum number of changes</span>
                  </div>
                )}
              </div>
            )}
          </form>
        </div>

        <div className="modal-footer">
          <button 
            type="button" 
            className="btn-secondary" 
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn-primary" 
            onClick={handleSubmit}
            disabled={isLoading || !username || username === user?.username || (availabilityStatus && !availabilityStatus.available) || (hasReachedLimit && !isCreating)}
          >
            {isLoading ? (
              <>
                <div className="spinner"></div>
                {isCreating ? 'Creating...' : 'Updating...'}
              </>
            ) : (
              isCreating ? 'Create Username' : 'Update Username'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UsernameModal;
